import type { ReactNode } from 'react'
import RenderHTML from '../../../../../../../components/ui-components/RenderHTML'
import { EventNames } from '../../../../../../../types/misc.types'
import { lightImpactVibrate, postCTA, trackEvent } from '../../../../../../../utils/appBridge'
import {
  LumpsumAllocationCategoryExecutionStatus,
  type LumpsumAllocationCategory
} from '../../../api/allocation.types'
import {
  AllocationMovementToggle,
  PlanCategoryBucketKey,
  type PlansActionFilter
} from '../../../api/types'
import { PlansLumpsumListEventIds, PlansScreen } from '../../../../analytics/plansEvents'
import {
  buildPlanAllocationRangeLabel,
  normalizePlanPercentage,
  planPercentageLabel
} from '../../../../display-values/lumpsumPlanPercentage'
import {
  getVisiblePlanCategoryBucketKeys,
  resolvePlanCategoryKey
} from '../../../../utils/lumpsum/planListUtils'
import { useLumpsumPlansContext } from '../../LumpsumPlansProvider'
import { CategoryCard } from '../../lumpsum-ui-components/CategoryCard'
import { AllocationBuyBucketAccordion } from './buckets/AllocationBuyBucketAccordion'
import { AllocationExecutedBucketAccordion } from './buckets/AllocationExecutedBucketAccordion'
import { AllocationInvalidatedBucketAccordion } from './buckets/AllocationInvalidatedBucketAccordion'
import { AllocationNoActionBucketAccordion } from './buckets/AllocationNoActionBucketAccordion'
import { AllocationSellBucketAccordion } from './buckets/AllocationSellBucketAccordion'
import { AllocationUnderReviewBucketAccordion } from './buckets/AllocationUnderReviewBucketAccordion'

type AllocationCategoryCardProps = {
  category: LumpsumAllocationCategory
  categoryIndex: number
  actionFilter: PlansActionFilter
  noDivider?: boolean
}

type AllocationBucketAccordionProps = {
  category: LumpsumAllocationCategory
  categoryId: string
  actionFilter: PlansActionFilter
  isBorderTop?: boolean
}

type AllocationCategoryHeaderProps = {
  category: LumpsumAllocationCategory
  label: string
}

export function AllocationCategoryCard({
  category,
  categoryIndex,
  actionFilter,
  noDivider = false
}: AllocationCategoryCardProps) {
  const { allocationMovementToggle } = useLumpsumPlansContext()
  const categoryKey = resolvePlanCategoryKey(category, categoryIndex)
  const label = category?.categoryName ?? categoryKey.replace(/_/g, ' ')
  const sharedBucketProps = {
    category,
    categoryId: categoryKey,
    actionFilter
  }

  return (
    <CategoryCard
      noDivider={noDivider}
      header={
        allocationMovementToggle === AllocationMovementToggle.FUND_MOVEMENT ? (
          <AllocationCategoryFundMovementHeader category={category} label={label} />
        ) : (
          <AllocationCategoryAllocationMovementHeader category={category} label={label} />
        )
      }
    >
      {getVisiblePlanCategoryBucketKeys(category, actionFilter).map((bucketKey, bucketIndex) =>
        renderAllocationPlanCategoryBucket(bucketKey, {
          ...sharedBucketProps,
          isBorderTop: bucketIndex > 0
        })
      )}
    </CategoryCard>
  )
}

export function AllocationCategoryFundMovementHeader({
  category,
  label
}: AllocationCategoryHeaderProps) {
  const fundMovement = category?.fundMovement
  const executionStatus = fundMovement?.executionStatus
  const currentFundCount = fundMovement?.currentFundCount ?? 0
  const idealFundCount = fundMovement?.idealAllocationFundCount?.absolute ?? 0
  const hasZeroFundCountEndpoint = currentFundCount === 0 || idealFundCount === 0

  if (executionStatus === LumpsumAllocationCategoryExecutionStatus.ACTIONABLE_INCREASE) {
    return (
      <AllocationCategoryHeaderLayout
        category={category}
        label={label}
        iconHtml={ALLOCATION_CATEGORY_FUND_DIVERSIFY_ICON_SVG}
        primaryText={`${formatFundCountLabel(currentFundCount)} → ${formatFundCountLabel(idealFundCount)}`}
        secondaryText={
          hasZeroFundCountEndpoint ? undefined : `Diversify to ${formatFundCountLabel(idealFundCount)}`
        }
      />
    )
  }

  if (executionStatus === LumpsumAllocationCategoryExecutionStatus.ACTIONABLE_DECREASE) {
    return (
      <AllocationCategoryHeaderLayout
        category={category}
        label={label}
        iconHtml={ALLOCATION_CATEGORY_FUND_CONSOLIDATE_ICON_SVG}
        primaryText={`${formatFundCountLabel(currentFundCount)} → ${formatFundCountLabel(idealFundCount)}`}
        secondaryText={
          hasZeroFundCountEndpoint ? undefined : `Consolidate to ${formatFundCountLabel(idealFundCount)}`
        }
      />
    )
  }

  if (executionStatus === LumpsumAllocationCategoryExecutionStatus.IN_PROGRESS) {
    return (
      <AllocationCategoryHeaderLayout
        category={category}
        label={label}
        iconHtml={ALLOCATION_CATEGORY_IN_PROGRESS_ICON_SVG}
        primaryText="In progress"
        secondaryText={`${idealFundCount} ideal no. of funds`}
      />
    )
  }

  if (executionStatus === LumpsumAllocationCategoryExecutionStatus.COMPLETED) {
    return (
      <AllocationCategoryHeaderLayout
        category={category}
        label={label}
        iconHtml={ALLOCATION_CATEGORY_IDEAL_ICON_SVG}
        primaryText="Ideal no. of funds"
        secondaryText={formatFundCountLabel(currentFundCount)}
      />
    )
  }

  if (executionStatus === LumpsumAllocationCategoryExecutionStatus.ALREADY_IDEAL) {
    return (
      <AllocationCategoryHeaderLayout
        category={category}
        label={label}
        iconHtml={ALLOCATION_CATEGORY_IDEAL_ICON_SVG}
        primaryText="Ideal no. of funds"
        secondaryText={formatFundCountLabel(currentFundCount)}
      />
    )
  }

  return <AllocationCategoryHeaderLayout category={category} label={label} />
}

export function AllocationCategoryAllocationMovementHeader({
  category,
  label
}: AllocationCategoryHeaderProps) {
  const allocationMovement = category?.allocationMovement
  const executionStatus = allocationMovement?.executionStatus
  const currentAllocationPercentage = allocationMovement?.currentAllocationPercentage ?? 0
  const idealAbsolutePercentage = allocationMovement?.idealAllocationPercentage?.absolute ?? 0
  const idealAllocationRangeLabel = buildPlanAllocationRangeLabel({
    idealAllocation:
      allocationMovement?.idealAllocationPercentage
  })
  const areBothAllocationPercentagesZero =
    normalizePlanPercentage(currentAllocationPercentage) === 0 &&
    normalizePlanPercentage(idealAbsolutePercentage) === 0

  if (executionStatus === LumpsumAllocationCategoryExecutionStatus.ACTIONABLE_INCREASE) {
    const allocationDiff = Math.abs(
      normalizePlanPercentage(idealAbsolutePercentage) -
      normalizePlanPercentage(currentAllocationPercentage)
    )

    return (
      <AllocationCategoryHeaderLayout
        category={category}
        label={label}
        iconHtml={ALLOCATION_CATEGORY_PERCENTAGE_ACTIONABLE_ICON_SVG}
        primaryText={`${planPercentageLabel({ rawPercentage: currentAllocationPercentage })} → ${planPercentageLabel({ rawPercentage: idealAbsolutePercentage })}`}
        secondaryText={
          areBothAllocationPercentagesZero
            ? undefined
            : `Increase by ${planPercentageLabel({ rawPercentage: allocationDiff })}`
        }
      />
    )
  }

  if (executionStatus === LumpsumAllocationCategoryExecutionStatus.ACTIONABLE_DECREASE) {
    const allocationDiff = Math.abs(
      normalizePlanPercentage(idealAbsolutePercentage) -
      normalizePlanPercentage(currentAllocationPercentage)
    )

    return (
      <AllocationCategoryHeaderLayout
        category={category}
        label={label}
        iconHtml={ALLOCATION_CATEGORY_PERCENTAGE_ACTIONABLE_ICON_SVG}
        primaryText={`${planPercentageLabel({ rawPercentage: currentAllocationPercentage })} → ${planPercentageLabel({ rawPercentage: idealAbsolutePercentage })}`}
        secondaryText={
          areBothAllocationPercentagesZero
            ? undefined
            : `Decrease by ${planPercentageLabel({ rawPercentage: allocationDiff })}`
        }
      />
    )
  }

  if (executionStatus === LumpsumAllocationCategoryExecutionStatus.IN_PROGRESS) {
    return (
      <AllocationCategoryHeaderLayout
        category={category}
        label={label}
        iconHtml={ALLOCATION_CATEGORY_IN_PROGRESS_ICON_SVG}
        primaryText="In progress"
        secondaryText={`${idealAllocationRangeLabel.length > 0 ? idealAllocationRangeLabel : '0%'} ideal allocation`}
      />
    )
  }

  if (executionStatus === LumpsumAllocationCategoryExecutionStatus.COMPLETED) {
    return (
      <AllocationCategoryHeaderLayout
        category={category}
        label={label}
        iconHtml={ALLOCATION_CATEGORY_IDEAL_ICON_SVG}
        primaryText="Reached ideal allocation"
        secondaryText={idealAllocationRangeLabel}
      />
    )
  }

  if (executionStatus === LumpsumAllocationCategoryExecutionStatus.ALREADY_IDEAL) {
    return (
      <AllocationCategoryHeaderLayout
        category={category}
        label={label}
        iconHtml={ALLOCATION_CATEGORY_IDEAL_ICON_SVG}
        primaryText="Ideal allocation"
        secondaryText={idealAllocationRangeLabel}
      />
    )
  }

  return <AllocationCategoryHeaderLayout category={category} label={label} />
}

export function AllocationCategoryHeaderLayout({
  category,
  label,
  iconHtml,
  primaryText,
  secondaryText
}: {
  category: LumpsumAllocationCategory
  label: string
  iconHtml?: string
  primaryText?: string
  secondaryText?: string
}) {
  const { allocationPlansData } = useLumpsumPlansContext()
  const hasStatus = Boolean(iconHtml && primaryText)

  function handleViewStrategyClick() {
    const slotBottomsheetCta = allocationPlansData?.slotBottomsheetCta
    if (slotBottomsheetCta == null || typeof slotBottomsheetCta !== 'object') return

    const group = category?.group ?? ''
    const categoryKey = category?.category ?? ''
    const ctaRecord = { ...(slotBottomsheetCta as Record<string, unknown>) }
    if (typeof ctaRecord.url === 'string') {
      ctaRecord.url = ctaRecord.url
        .replace(/(group=)[^&]*/, `$1${encodeURIComponent(group)}`)
        .replace(/(category=)[^&]*/, `$1${encodeURIComponent(categoryKey)}`)
    }

    lightImpactVibrate()
    trackEvent({
      eventName: EventNames.UserClicked,
      payload: {
        ScreenName: PlansScreen.LumpsumAllocation,
        eventId: PlansLumpsumListEventIds.ViewStrategy,
        buttonLabel: 'View strategy'
      }
    })
    postCTA(JSON.stringify(ctaRecord))
  }

  return (
    <div className="flex items-start justify-between gap-3 px-5 py-4">
      <div className="flex min-w-0 flex-col gap-1">
        <div className="text-[14px] font-[500] leading-[140%] text-[#232323]">{label}</div>
        <div
          className="text-[13px] font-[500] leading-[140%] text-[#1470CC] cursor-pointer"
          onClick={handleViewStrategyClick}
        >
          View strategy
        </div>
      </div>
      {hasStatus ? (
        <div className="flex shrink-0 flex-col items-end gap-1">
          <div className="flex items-center justify-end gap-1">
            <RenderHTML
              innerHTMLText={iconHtml ?? ''}
              className="size-3 shrink-0 overflow-hidden"
            />
            <div className="text-[13px] font-[500] leading-[140%] text-[#232323] whitespace-nowrap">
              {primaryText}
            </div>
          </div>
          {secondaryText ? (
            <div className="text-[13px] font-[400] leading-[140%] text-[#3D3D3D] whitespace-nowrap">
              {secondaryText}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

function renderAllocationPlanCategoryBucket(
  bucketKey: PlanCategoryBucketKey,
  bucketProps: AllocationBucketAccordionProps
): ReactNode {
  if (bucketKey === PlanCategoryBucketKey.sellRecommendations) {
    return <AllocationSellBucketAccordion key={bucketKey} {...bucketProps} />
  }
  if (bucketKey === PlanCategoryBucketKey.buyRecommendations) {
    return <AllocationBuyBucketAccordion key={bucketKey} {...bucketProps} />
  }
  if (bucketKey === PlanCategoryBucketKey.underReviewRecommendations) {
    return <AllocationUnderReviewBucketAccordion key={bucketKey} {...bucketProps} />
  }
  if (bucketKey === PlanCategoryBucketKey.executedRecommendations) {
    return <AllocationExecutedBucketAccordion key={bucketKey} {...bucketProps} />
  }
  if (bucketKey === PlanCategoryBucketKey.invalidatedRecommendations) {
    return <AllocationInvalidatedBucketAccordion key={bucketKey} {...bucketProps} />
  }
  if (bucketKey === PlanCategoryBucketKey.noActionFunds) {
    return <AllocationNoActionBucketAccordion key={bucketKey} {...bucketProps} />
  }
  return null
}

function formatFundCountLabel(fundCount: number): string {
  return `${fundCount} fund${fundCount === 1 ? '' : 's'}`
}

export const ALLOCATION_CATEGORY_FUND_DIVERSIFY_ICON_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
  <path d="M6 12C2.68632 12 2.34843e-07 9.31371 5.24537e-07 6C8.14228e-07 2.68632 2.68632 -8.14228e-07 6 -5.24537e-07C9.31371 -2.34843e-07 12 2.68632 12 6C12 9.31371 9.31371 12 6 12ZM5.35938 9.04297C5.67949 9.38235 6.19853 9.3825 6.51855 9.04297L8.51758 6.91992C8.77549 6.64596 8.59329 6.17811 8.22852 6.17773L6.75879 6.17773L6.75879 3.56738C6.75863 3.08701 6.39108 2.69727 5.93848 2.69727C5.48596 2.69736 5.1193 3.08707 5.11914 3.56738L5.11914 6.17773L3.64941 6.17773C3.28449 6.17782 3.10171 6.64582 3.35938 6.91992L5.35938 9.04297Z" fill="#3D3D3D"/>
</svg>`

export const ALLOCATION_CATEGORY_FUND_CONSOLIDATE_ICON_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
  <path d="M6 0C9.31368 0 12 2.68629 12 6C12 9.31368 9.31368 12 6 12C2.68629 12 0 9.31368 0 6C1.28851e-07 2.68629 2.68629 1.28855e-07 6 0ZM6.64062 2.95703C6.32051 2.61765 5.80147 2.6175 5.48145 2.95703L3.48242 5.08008C3.22451 5.35404 3.40671 5.82189 3.77148 5.82227H5.24121V8.43262C5.24137 8.91299 5.60892 9.30273 6.06152 9.30273C6.51404 9.30264 6.8807 8.91293 6.88086 8.43262V5.82227H8.35059C8.71551 5.82218 8.89829 5.35418 8.64062 5.08008L6.64062 2.95703Z" fill="#3D3D3D"/>
</svg>`

export const ALLOCATION_CATEGORY_PERCENTAGE_ACTIONABLE_ICON_SVG = `<svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M5.2831 1.92969C4.33555 1.92969 3.40927 2.21067 2.62141 2.7371C1.83355 3.26353 1.21949 4.01177 0.856876 4.88719C0.494263 5.76262 0.399387 6.72591 0.584246 7.65526C0.769104 8.5846 1.22539 9.43826 1.89541 10.1083C2.56544 10.7783 3.41909 11.2346 4.34844 11.4194C5.27778 11.6043 6.24108 11.5094 7.1165 11.1468C7.99192 10.7842 8.74016 10.1701 9.26659 9.38228C9.79303 8.59442 10.074 7.66815 10.074 6.7206H5.52264C5.45911 6.7206 5.39818 6.69536 5.35326 6.65044C5.30834 6.60551 5.2831 6.54458 5.2831 6.48105V1.92969Z" fill="#3D3D3D"/><path d="M6.7168 5.03955C6.7168 5.10318 6.74203 5.16411 6.78696 5.20903C6.83188 5.25395 6.89281 5.27919 6.95634 5.27919H11.5077C11.5077 4.00856 11.003 2.78998 10.1045 1.89151C9.20601 0.993036 7.98743 0.488281 6.7168 0.488281V5.03955Z" fill="#3D3D3D"/></svg>`

export const ALLOCATION_CATEGORY_IN_PROGRESS_ICON_SVG = `<svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg"><circle opacity="0.2" cx="6" cy="6" r="6" fill="#232323"/><circle cx="6" cy="6" r="2" fill="#232323"/></svg>`

export const ALLOCATION_CATEGORY_IDEAL_ICON_SVG = `<svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M6 0C4.81332 0 3.65328 0.351894 2.66658 1.01118C1.67989 1.67047 0.910851 2.60754 0.456726 3.7039C0.0025997 4.80026 -0.11622 6.00666 0.115291 7.17054C0.346802 8.33443 0.918247 9.40353 1.75736 10.2426C2.59648 11.0818 3.66558 11.6532 4.82946 11.8847C5.99335 12.1162 7.19975 11.9974 8.2961 11.5433C9.39246 11.0892 10.3295 10.3201 10.9888 9.33342C11.6481 8.34673 12 7.18669 12 6C11.9981 4.40929 11.3653 2.88427 10.2405 1.75946C9.11573 0.634658 7.59072 0.00190543 6 0V0ZM8.9304 5.0304L5.6304 8.3304C5.56076 8.40007 5.47807 8.45533 5.38706 8.49303C5.29605 8.53073 5.19851 8.55014 5.1 8.55014C5.0015 8.55014 4.90395 8.53073 4.81294 8.49303C4.72194 8.45533 4.63925 8.40007 4.5696 8.3304L3.0696 6.8304C2.92893 6.68973 2.8499 6.49894 2.8499 6.3C2.8499 6.10106 2.92893 5.91027 3.0696 5.7696C3.21027 5.62893 3.40106 5.5499 3.6 5.5499C3.79894 5.5499 3.98973 5.62893 4.1304 5.7696L5.1 6.7392L7.8696 3.9696C7.93926 3.89995 8.02195 3.8447 8.11295 3.807C8.20396 3.7693 8.3015 3.7499 8.4 3.7499C8.49851 3.7499 8.59605 3.7693 8.68705 3.807C8.77806 3.8447 8.86075 3.89995 8.9304 3.9696C9.00006 4.03925 9.05531 4.12194 9.093 4.21295C9.1307 4.30396 9.1501 4.4015 9.1501 4.5C9.1501 4.5985 9.1307 4.69604 9.093 4.78705C9.05531 4.87806 9.00006 4.96075 8.9304 5.0304V5.0304Z" fill="#3D3D3D"/></svg>`
